import { Injectable, signal } from '@angular/core';
import { ViewTrackingService, ViewType } from './view-tracking.service';

/**
 * Base service for managing view counts functionality
 * Provides reusable methods to avoid code duplication across components
 */
@Injectable({
  providedIn: 'root'
})
export class ViewCountBaseService {
  
  constructor(private viewTrackingService: ViewTrackingService) {}

  /**
   * Creates a view counts signal for managing view count state
   */
  createViewCountsSignal() {
    return signal<{ [key: number]: number }>({});
  }

  /**
   * Gets the view count for a specific entity
   * @param viewCounts Signal containing view counts
   * @param entityId ID of the entity
   * @returns View count for the entity
   */
  getViewCount(viewCounts: any, entityId: number): number {
    return viewCounts()[entityId] || 0;
  }

  /**
   * Loads view counts for a list of entities
   * @param entities Array of entities with ID property
   * @param viewType Type of view (Profile or JobOffer)
   * @param viewCounts Signal to update with view counts
   * @param idProperty Property name containing the entity ID (default: 'id')
   */
  loadViewCounts<T extends Record<string, any>>(
    entities: T[], 
    viewType: ViewType, 
    viewCounts: any,
    idProperty: keyof T = 'id'
  ): void {
    entities.forEach(entity => {
      const entityId = entity[idProperty] as number;
      this.viewTrackingService.getViewsCount(viewType, entityId).subscribe({
        next: (count) => {
          const currentCounts = viewCounts();
          viewCounts.set({
            ...currentCounts,
            [entityId]: count
          });
        },
        error: (error) => {
          console.error(`Error loading view count for ${viewType} ${entityId}:`, error);
          const currentCounts = viewCounts();
          viewCounts.set({
            ...currentCounts,
            [entityId]: 0
          });
        }
      });
    });
  }

  /**
   * Loads view counts for entities and assigns them directly to the entity property
   * @param entities Array of entities with ID property  
   * @param viewType Type of view (Profile or JobOffer)
   * @param idProperty Property name containing the entity ID (default: 'id')
   * @param countProperty Property name to store the count (default: 'viewsCount')
   */
  loadViewCountsDirectly<T extends Record<string, any>>(
    entities: T[], 
    viewType: ViewType, 
    idProperty: keyof T = 'id',
    countProperty: keyof T = 'viewsCount'
  ): void {
    entities.forEach(entity => {
      const entityId = entity[idProperty] as number;
      this.viewTrackingService.getViewsCount(viewType, entityId).subscribe({
        next: (count) => {
          (entity as any)[countProperty] = count;
        },
        error: (error) => {
          console.error(`Error loading view count for ${viewType} ${entityId}:`, error);
          (entity as any)[countProperty] = 0;
        }
      });
    });
  }
}